import { useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

import { getBusinessDispatchWindows } from "../../services/businessNotificationService";

// F9: receiving hours (Delivery Setup) and Rider shifts (HR) are separate on
// purpose. A trip can only leave while both still cover it, so this shows the
// overlap per day instead of making owners combine the two in their heads.

const WARNING_COPY = Object.freeze({
  RIDERS_WITHOUT_RECEIVING: "Riders are scheduled, but the store is not receiving returning Riders this day, so no trips can go out.",
  RECEIVING_WITHOUT_RIDERS: "The store is receiving, but no Rider has a shift this day.",
  NO_OVERLAP: "Rider shifts and receiving hours do not overlap this day, so no trips can go out.",
  OVERLAP_TOO_SHORT: "Riders and receiving hours overlap for less than one typical trip.",
});

export function formatTime(value, timeZone) {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-PH", { hour: "numeric", minute: "2-digit", timeZone }).format(new Date(value));
}

function range(window, timeZone) {
  return window ? `${formatTime(window.startsAt || window.opensAt, timeZone)}–${formatTime(window.endsAt || window.closesAt, timeZone)}` : null;
}

function dayLabel(serviceDate, index, timeZone) {
  const date = new Date(`${serviceDate}T12:00:00Z`);
  const name = new Intl.DateTimeFormat("en-PH", { weekday: "long", month: "short", day: "numeric", timeZone }).format(date);
  return index === 0 ? `Today, ${name}` : name;
}

function Row({ label, children, muted = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
      <dt className="text-neutral-500 dark:text-neutral-400">{label}</dt>
      <dd className={`text-right font-medium ${muted ? "text-neutral-400 dark:text-neutral-500" : ""}`}>{children}</dd>
    </div>
  );
}

export default function DispatchWindowsSummary({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    getBusinessDispatchWindows()
      .then((response) => { if (active) { setData(response?.data || null); setState("ready"); } })
      .catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [refreshKey]);

  const tz = data?.timezone || undefined;
  const tripMinutes = data?.trip?.seconds ? Math.round(data.trip.seconds / 60) : null;

  return (
    <section className="max-w-3xl rounded-2xl border border-neutral-200 p-5 dark:border-neutral-800" aria-labelledby="dispatch-windows-title" data-testid="dispatch-windows">
      <h3 id="dispatch-windows-title" className="flex items-center gap-2 font-semibold"><CalendarClock className="h-5 w-5" aria-hidden="true" /> When trips can go out</h3>
      <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
        A trip only leaves if the Rider can be back and handed over before their shift ends and before the store stops receiving.
        Receiving hours are set here; Rider shifts are set in <Link to="/business/hr/shifts" className="underline">HR › Shifts</Link>.
        {tripMinutes ? ` A typical trip here takes about ${tripMinutes} min there and back.` : ""}
      </p>

      {state === "loading" ? <p role="status" className="mt-4 flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Checking the week…</p> : null}
      {state === "error" ? <p role="alert" className="mt-4 text-sm text-danger">The schedule overview could not be loaded. Refresh the page to try again.</p> : null}
      {state === "ready" && data && !data.available ? (
        <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-300">
          {data.reason === "NO_TIMEZONE" ? "Set your business time zone in HR settings to see when trips can go out." : "Save your dispatch location first; the delivery rules come with it."}
        </p>
      ) : null}

      {state === "ready" && data?.available ? (
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {data.days.map((day, index) => (
            <li key={day.serviceDate} className="rounded-xl border border-neutral-200 p-3 dark:border-neutral-800" data-testid="dispatch-day">
              <p className="font-semibold">{dayLabel(day.serviceDate, index, tz)}</p>
              <dl className="mt-1 divide-y divide-neutral-100 dark:divide-neutral-800">
                <Row label="Receiving" muted={!day.receiving}>{range(day.receiving, tz) || "Closed"}</Row>
                <Row label="Rider coverage" muted={!day.coverage}>
                  {day.riders.length
                    ? day.riders.map((rider) => `${rider.name} ${range(rider, tz)}`).join(", ")
                    : "No Rider shifts"}
                </Row>
                <Row label="Trips can leave" muted={!day.effective}>{range(day.effective, tz) ? `${formatTime(day.effective.startsAt, tz)}–about ${formatTime(day.latestDepartureAt || day.effective.endsAt, tz)}` : "Not this day"}</Row>
                <Row label="Latest new trip" muted={!day.latestDepartureAt}>{day.latestDepartureAt ? `about ${formatTime(day.latestDepartureAt, tz)}` : "None"}</Row>
              </dl>
              {day.warnings.map((code) => (
                <p key={code} className="mt-2 flex gap-2 rounded-lg bg-warning-subtle p-2 text-xs text-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />{WARNING_COPY[code] || "Check this day's hours and shifts."}
                </p>
              ))}
              {day.riders.filter((rider) => rider.overlap && rider.endsAfterReceiving).map((rider) => (
                <p key={`late-${rider.riderMemberId}`} className="mt-2 text-xs text-neutral-600 dark:text-neutral-300">
                  {rider.name}'s shift ends after the store stops receiving ({formatTime(day.receiving.closesAt, tz)}); trips stop earlier than the shift.
                </p>
              ))}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
